import { useCallback, useMemo, useState } from 'react';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { ParsedCountry } from 'react-international-phone';
import {
  ApplicationResponse,
  AzureUserError,
  EndUserRequest,
  GenericValidationError,
  GivenRoleDto,
  OrganizationResponse,
  ResponseError
} from 'api/minside';
import { usersClient } from 'api/MinSideClients';
import { useUserApplications } from 'api/hooks/useUserApplications';
import { useRoles } from 'api/hooks/useRoles';
import { useOrganizationsV2 } from 'api/hooks/useOrganizationsV2';
import { useTranslation } from 'react-i18next';
import { SelectOption } from 'common.ui';
import { emailRegex, blockedEmailDomainPattern } from 'utils/emailValidation';

type UserFormValues = {
  applicationId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  givenRole?: GivenRoleDto;
};

const emptyForm: UserFormValues = {
  applicationId: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  givenRole: undefined
};

const phoneUtil = PhoneNumberUtil.getInstance();

export function useCreateEndUser() {
  const { t } = useTranslation('pages');

  const [apiErrors, setApiErrors] = useState<GenericValidationError>();
  const [resultModal, setResultModal] = useState<{ title: string; message: string } | null>(null);
  const [values, setValues] = useState<UserFormValues>(emptyForm);
  const [role, setRole] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState('');
  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [phone, setPhone] = useState('');
  const [parsedPhone, setParsedPhone] = useState('');
  const [isValidPhone, setIsValidPhone] = useState(true);
  const [isValidOrganization, setIsValidOrganization] = useState(true);
  const [resetKey, setResetKey] = useState(0);

  const [applications] = useUserApplications();
  const selectedApplication: ApplicationResponse | undefined = applications?.find(
    (a) => a.id === values.applicationId
  );
  const [roles, roleData, isRolesLoading] = useRoles(selectedApplication?.id, false, true);
  const [organizationGroupIds, setOrganizationGroupIds] = useState<number[] | null | undefined>();
  const [organizations] = useOrganizationsV2(organizationGroupIds ?? [], [organizationGroupIds]);

  // Auto-velg applikasjon dersom kun én er tilgjengelig.
  // Settes under render (betinget, så den stopper av seg selv) i stedet for i en effekt.
  const onlyApplicationId = applications?.length === 1 ? applications[0].id : undefined;
  if (onlyApplicationId && !values.applicationId) {
    setValues((v) => ({ ...v, applicationId: onlyApplicationId }));
  }

  // Reset eller auto-velg rolle når tilgjengelige roller endres (f.eks. ved applikasjonsbytte).
  // Gjøres under render når rollelisten endres, i stedet for i en effekt.
  const [previousRoles, setPreviousRoles] = useState<{ roles: typeof roles; roleData: typeof roleData }>({
    roles: undefined,
    roleData: undefined
  });
  if (previousRoles.roles !== roles || previousRoles.roleData !== roleData) {
    setPreviousRoles({ roles, roleData });
    if (roles !== undefined) {
      if (roles.length === 1) {
        const singleRoleId = roles[0].id ?? '';
        setRole(singleRoleId);
        setSelectedOrganization('');
        setIsValidOrganization(true);
        const orgGroupIds = roleData?.find((d) => d.roleId?.toString() === singleRoleId);
        setOrganizationGroupIds(orgGroupIds?.organizationGroupIds);
      } else {
        setRole('');
        setOrganizationGroupIds(undefined);
        setSelectedOrganization('');
      }
    }
  }

  const organizationOptions = useMemo(
    () =>
      organizations?.map((org: OrganizationResponse) => ({
        id: org.organizationId?.toString(),
        text: org.name
      })) as SelectOption[] ?? [],
    [organizations]
  );

  // Auto-velg organisasjon dersom kun én er tilgjengelig (betinget under render, i stedet for i en effekt)
  const onlyOrganizationId = organizationOptions.length === 1 ? organizationOptions[0].id : undefined;
  if (onlyOrganizationId && !selectedOrganization) {
    setSelectedOrganization(onlyOrganizationId);
    setIsValidOrganization(true);
  }

  const shouldRenderOrganizationDropdown = useMemo(
    () => !!organizationGroupIds?.length && organizationOptions.length > 0,
    [organizationGroupIds, organizationOptions.length]
  );

  const validatePhone = useCallback(
    (phoneNumber: string) => {
      try {
        const region = phoneUtil.getRegionCodeForNumber(phoneUtil.parseAndKeepRawInput(phoneNumber));
        return phoneUtil.isValidNumberForRegion(phoneUtil.parseAndKeepRawInput(phoneNumber, region), region);
      } catch {
        return false;
      }
    },
    []
  );

  const validateEmail = (input: string) => {
    setEmail(input);
    setIsValidEmail(emailRegex.test(input) && !blockedEmailDomainPattern.test(input));
  };

  const handlePhoneChange = (newPhone: string, meta: { country: ParsedCountry; inputValue: string }) => {
    const digits = meta.inputValue.split(' ').slice(1).join('');
    setParsedPhone('+' + meta.country.dialCode + ' ' + digits);
    setPhone(newPhone);
  };

  const handleRoleChange = (roleId: string) => {
    setRole(roleId);
    setSelectedOrganization('');
    setIsValidOrganization(true);
    const orgGroupIds = roleData?.find((d) => d.roleId?.toString() === roleId);
    setOrganizationGroupIds(orgGroupIds?.organizationGroupIds);
  };

  const handleApplicationChange = (appId: string) => {
    setValues((v) => ({ ...v, applicationId: appId }));
  };

  const resetForm = (resetFn: () => void) => {
    setValues((prev) => ({ ...emptyForm, applicationId: prev.applicationId }));
    setPhone('');
    setEmail('');
    setApiErrors(undefined);
    setResetKey((k) => k + 1);
    resetFn();
  };

  const showModal = (title: string, message: string) => {
    setResultModal({ title, message });
  };

  const handleSubmit = async (model: UserFormValues, resetFn: () => void) => {
    if (!validatePhone(phone)) {
      setIsValidPhone(false);
      return;
    }

    if (shouldRenderOrganizationDropdown && !selectedOrganization) {
      setIsValidOrganization(false);
      return;
    }

    const userRequest: EndUserRequest = {
      ...model,
      email: isValidEmail ? email : '',
      phoneNumber: parsedPhone,
      givenRole: {
        id: role ? parseInt(role) : undefined,
        organizationId: shouldRenderOrganizationDropdown && selectedOrganization ? parseInt(selectedOrganization) : undefined,
        expiredAt: undefined
      }
    };

    try {
      const response = await usersClient.endUserPost({ endUserRequest: userRequest });

      if (response.status === 'RoleAdded') {
        showModal(t('createUser.message.roleAddedTitle'), t('createUser.message.roleAdded'));
      } else if (response.status === 'AlreadyHasRole') {
        showModal(t('createUser.message.alreadyHasRoleTitle'), t('createUser.message.alreadyHasRole'));
      } else {
        showModal(t('createUser.message.title'), t('createUser.message.info', { displayName: response.displayName }));
      }

      resetForm(resetFn);
    } catch (e) {
      if (e instanceof ResponseError && e.response.status === 400) {
        const body = await e.response.json();
        if (body?.error === 'EndUser_BlockedDomain') {
          setApiErrors(undefined);
          showModal(t('createUser.message.blockedDomainTitle'), t('createUser.message.blockedDomain'));
        } else if (body?.error === 'EndUser_FederatedDomainUserMustExist') {
          setApiErrors(undefined);
          showModal(
            t('createUser.message.federatedDomainUserMustExistTitle'),
            t('createUser.message.federatedDomainUserMustExist')
          );
        } else if (body?.error === 'EndUser_TargetUserMustBeExternal') {
          setApiErrors(undefined);
          showModal(t('createUser.message.targetUserMustBeExternalTitle'), t('createUser.message.targetUserMustBeExternal'));
        } else if (body?.error === 'EndUser_MissingEmail') {
          setApiErrors(undefined);
          showModal(t('createUser.message.missingEmailTitle'), t('createUser.message.missingEmail'));
        } else {
          const result = body as GenericValidationError;
          if (result) setApiErrors(result);
        }
      }
      if (e instanceof ResponseError && e.response.status === 409) {
        const result = (await e.response.json()) as AzureUserError;
        if (result) {
          setApiErrors(undefined);
          showModal('Error', result.errors?.email || result.errors?.phoneNumber || '');
        }
      }
    }
  };

  return {
    values,
    setValues,
    apiErrors,
    resultModal,
    closeModal: () => setResultModal(null),
    applications,
    roles,
    roleData,
    organizationOptions,
    shouldRenderOrganizationDropdown,
    organizationGroupIds,
    phone,
    isValidPhone,
    setIsValidPhone,
    isValidOrganization,
    setIsValidOrganization,
    email,
    isValidEmail,
    role,
    selectedOrganization,
    setSelectedOrganization,
    resetKey,
    isRolesLoading,
    handlePhoneChange,
    handleRoleChange,
    handleApplicationChange,
    validateEmail,
    handleSubmit
  };
}
